import type { ColorMode } from "@chakra-ui/react";
import { DEFAULT_THEME_PREFERENCE } from "theme";
import { THEME_PREFERENCE_STORAGE_KEY } from "utils/localStorage";

export type ThemePreference = "light" | "dark" | "system";

export const DARK_MODE_MEDIA_QUERY = "(prefers-color-scheme: dark)";

const isThemePreference = (value: string | null): value is ThemePreference =>
  value === "light" || value === "dark" || value === "system";

export const getStoredPreference = (): ThemePreference => {
  if (typeof window === "undefined") {
    return DEFAULT_THEME_PREFERENCE;
  }

  try {
    const storedPreference = window.localStorage.getItem(
      THEME_PREFERENCE_STORAGE_KEY,
    );
    return isThemePreference(storedPreference)
      ? storedPreference
      : DEFAULT_THEME_PREFERENCE;
  } catch {
    return DEFAULT_THEME_PREFERENCE;
  }
};

export const getSystemColorMode = (): ColorMode => {
  if (
    typeof window === "undefined" ||
    typeof window.matchMedia !== "function"
  ) {
    return "light";
  }

  return window.matchMedia(DARK_MODE_MEDIA_QUERY).matches ? "dark" : "light";
};

export const resolveColorMode = (
  preference: ThemePreference,
  systemColorMode: ColorMode,
): ColorMode => (preference === "system" ? systemColorMode : preference);

export const persistPreference = (preference: ThemePreference) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(THEME_PREFERENCE_STORAGE_KEY, preference);
  } catch {
    // Ignore write failures so theme selection still works in-memory.
  }
};

/**
 * The theme as PostHog properties: the saved preference and the color mode the
 * user actually sees ("system" resolved against the OS setting).
 */
export const toThemeAnalyticsProperties = (
  preference: ThemePreference,
  effectiveColorMode: ColorMode,
) => ({
  theme_preference: preference,
  theme_effective: effectiveColorMode,
});

/**
 * Current theme properties for code that reads them once and must not run again
 * on a theme change, such as the `posthog.identify` call in `AnalyticsIdentity`.
 * Reads the same sources `ThemePreferenceProvider` starts from.
 */
export const getThemeAnalyticsProperties = () => {
  const preference = getStoredPreference();
  return toThemeAnalyticsProperties(
    preference,
    resolveColorMode(preference, getSystemColorMode()),
  );
};
